import produce, { Draft } from 'immer';
import { forEach } from 'lodash';

import { Action } from '../actions';
import { StrategyEditorActionType } from '../actions/strategyEditor';
import { mapStringToCurrencySymbol } from '../mappers/common/common';
import { mapStrategyForEditor } from '../mappers/strategyEditor';
import { parseNullableNumber } from '../service/FormatHelpers/FormatHelpers';
import {
    hasAvgClickPrice,
    hasAvgClickPriceEnabled,
    hasAvgCpaPrice,
    hasContextLimit,
    hasDailyBudget,
    hasGoalId,
    hasMaxClickPrice,
    hasOptimizationCriteria,
    hasSeparateBidding,
    hasWeekBundle,
    hasWeeklyBudget,
    hasWeeklyBudgetEnabled,
    isStrategyRoi,
    makeEmptyStrategy
} from '../service/StrategyEditorHelpers/StrategyEditorHelpers';
import {
    validateAvgClickPrice,
    validateAvgCpaPrice,
    validateDailyBudget,
    validateMaxClickPrice,
    validateProfitability,
    validateRoiCoef,
    validateWeekBundle,
    validateWeeklyBudget
} from '../service/StrategyEditorValidators/StrategyEditorValidators';
import { State } from '../typings/state';
import { StrategyErrorType, StrategyForEditor } from '../typings/strategy';
import { StrategyEditorState } from '../typings/strategyEditor';

export const strategyEditorInitialState = {
    editors: {}
};

export const strategyEdiorReducer = (
    state: StrategyEditorState = strategyEditorInitialState,
    action: Action,
    fullState: Readonly<State>
): StrategyEditorState => {
    return produce(state, (draft: Draft<StrategyEditorState>) => {
        switch (action.type) {
            case StrategyEditorActionType.INIT: {
                action.payload.forEach(
                    ({ editorId, strategy }): void => {
                        draft.editors[editorId] = {
                            strategyType: strategy.type,
                            platform: strategy.platform,
                            strategies: {
                                [strategy.type]: strategy
                            },
                            isOpen: false,
                            isOptimizationParamsOpen: true,
                            isDailyBudgetParamsOpen: false,
                            isAuxiliaryParamsOpen: false
                        };
                    }
                );

                break;
            }

            case StrategyEditorActionType.OPEN: {
                const { editorId } = action;

                draft.editors[editorId].isOpen = true;

                break;
            }

            case StrategyEditorActionType.CLOSE: {
                const { editorId } = action;

                draft.editors[editorId].isOpen = false;

                break;
            }

            case StrategyEditorActionType.CHANGE_TYPE: {
                const editor = draft.editors[action.editorId];
                if (!editor) {
                    return;
                }

                editor.strategyType = action.strategyType;

                if (!editor.strategies[action.strategyType]) {
                    editor.strategies[action.strategyType] = makeEmptyStrategy(
                        action.strategyType,
                        editor.platform
                    );
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_PLATFORM: {
                const editor = draft.editors[action.editorId];
                if (!editor) {
                    return;
                }

                editor.platform = action.platform;
                forEach(
                    editor.strategies,
                    (strategy: StrategyForEditor): void => {
                        strategy.platform = action.platform;
                    }
                );
                break;
            }

            case StrategyEditorActionType.CHANGE_DAILY_BUDGET: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasDailyBudget(strategy)) {
                    strategy.dailyBudget = {
                        inputVal: action.dailyBudget,
                        numVal: parseNullableNumber(action.dailyBudget)
                    };
                }

                break;
            }

            case StrategyEditorActionType.BLUR_DAILY_BUDGET: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                const currency = mapStringToCurrencySymbol(
                    fullState.commonData.client.workCurrency
                );

                if (hasDailyBudget(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.dailyBudget
                    ] = validateDailyBudget(strategy, constants, currency);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_GOAL_ID: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasGoalId(strategy)) {
                    strategy.goalId = action.goalId;
                    delete strategy.errors[StrategyErrorType.goalId];
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_DAILY_BUDGET_MODE: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasDailyBudget(strategy)) {
                    strategy.dailyBudgetMode = action.dailyBudgetMode;

                    delete strategy.errors[StrategyErrorType.dailyBudget];
                    delete strategy.errors[StrategyErrorType.dailyBudgetParams];
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_AVG_CLICK_PRICE: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasAvgClickPrice(strategy)) {
                    strategy.avgClickPrice = {
                        inputVal: action.avgClickPrice,
                        numVal: parseNullableNumber(action.avgClickPrice)
                    };

                    delete strategy.errors[StrategyErrorType.avgClickPrice];
                    delete strategy.errors[
                        StrategyErrorType.optimizationParams
                    ];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_AVG_CLICK_PRICE: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                const currency = mapStringToCurrencySymbol(
                    fullState.commonData.client.workCurrency
                );

                if (hasAvgClickPrice(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.avgClickPrice
                    ] = validateAvgClickPrice(strategy, constants, currency);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_WEEK_BUNDLE: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasWeekBundle(strategy)) {
                    strategy.weekBundle = {
                        inputVal: action.weekBundle,
                        numVal: parseNullableNumber(action.weekBundle)
                    };

                    delete strategy.errors[StrategyErrorType.weekBundle];
                    delete strategy.errors[
                        StrategyErrorType.optimizationParams
                    ];
                }
                break;
            }

            case StrategyEditorActionType.BLUR_WEEK_BUNDLE: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasWeekBundle(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.weekBundle
                    ] = validateWeekBundle(strategy, constants);
                }
                break;
            }

            case StrategyEditorActionType.TOGGLE_WEEKLY_BUDGET_ENABLED: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasWeeklyBudgetEnabled(strategy)) {
                    strategy.weeklyBudgetEnabled = !strategy.weeklyBudgetEnabled;
                }
                break;
            }

            case StrategyEditorActionType.TOGGLE_AVG_CLICK_PRICE_ENABLED: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasAvgClickPriceEnabled(strategy)) {
                    strategy.avgClickPriceEnabled = !strategy.avgClickPriceEnabled;
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_OPTIMIZATION_CRITERIA: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasOptimizationCriteria(strategy)) {
                    strategy.optimizationCriteria = action.optimizationCriteria;

                    delete strategy.errors[
                        StrategyErrorType.optimizationParams
                    ];
                    delete strategy.errors[StrategyErrorType.avgClickPrice];
                    delete strategy.errors[StrategyErrorType.weeklyBudget];
                    delete strategy.errors[StrategyErrorType.weekBundle];
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_AVG_CPA_PRICE: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasAvgCpaPrice(strategy)) {
                    strategy.avgCpaPrice = {
                        inputVal: action.avgCpaPrice,
                        numVal: parseNullableNumber(action.avgCpaPrice)
                    };

                    delete strategy.errors[StrategyErrorType.avgCpaPrice];
                    delete strategy.errors[
                        StrategyErrorType.optimizationParams
                    ];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_AVG_CPA_PRICE: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                const currency = mapStringToCurrencySymbol(
                    fullState.commonData.client.workCurrency
                );

                if (hasAvgCpaPrice(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.avgCpaPrice
                    ] = validateAvgCpaPrice(strategy, constants, currency);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_WEEKLY_BUDGET: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasWeeklyBudget(strategy)) {
                    strategy.weeklyBudget = {
                        inputVal: action.weeklyBudget,
                        numVal: parseNullableNumber(action.weeklyBudget)
                    };

                    delete strategy.errors[StrategyErrorType.weeklyBudget];
                    delete strategy.errors[
                        StrategyErrorType.optimizationParams
                    ];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_WEEKLY_BUDGET: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                const currency = mapStringToCurrencySymbol(
                    fullState.commonData.client.workCurrency
                );

                if (hasWeeklyBudget(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.weeklyBudget
                    ] = validateWeeklyBudget(strategy, constants, currency);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_SEPARATE_BIDDING: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasSeparateBidding(strategy)) {
                    strategy.separateBidding = !strategy.separateBidding;
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_CONTEXT_LIMIT: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasContextLimit(strategy)) {
                    strategy.contextLimit = action.contextLimit;
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_CPC_HOLD: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasContextLimit(strategy)) {
                    strategy.cpcHoldEnabled = !strategy.cpcHoldEnabled;
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_ROI_COEF: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (isStrategyRoi(strategy)) {
                    strategy.roiCoef = {
                        inputVal: action.roiCoef,
                        numVal: parseNullableNumber(action.roiCoef)
                    };

                    delete strategy.errors[StrategyErrorType.roiCoef];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_ROI_COEF: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (isStrategyRoi(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.roiCoef
                    ] = validateRoiCoef(strategy);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_RESERVE_RETURN: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (isStrategyRoi(strategy)) {
                    strategy.reserveReturn = action.reserveReturn;
                }

                break;
            }

            case StrategyEditorActionType.CHANGE_PROFITABILITY: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (isStrategyRoi(strategy)) {
                    strategy.profitability = {
                        inputVal: action.profitability,
                        numVal: parseNullableNumber(action.profitability)
                    };

                    delete strategy.errors[StrategyErrorType.profitability];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_PROFITABILITY: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (isStrategyRoi(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.profitability
                    ] = validateProfitability(strategy);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_MAX_CLICK_PRICE: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];

                if (hasMaxClickPrice(strategy)) {
                    strategy.maxClickPrice = {
                        inputVal: action.maxClickPrice,
                        numVal: parseNullableNumber(action.maxClickPrice)
                    };

                    delete strategy.errors[StrategyErrorType.maxClickPrice];
                }

                break;
            }

            case StrategyEditorActionType.BLUR_MAX_CLICK_PRICE: {
                const { editorId, constants } = action;
                const editor = draft.editors[editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                const currency = mapStringToCurrencySymbol(
                    fullState.commonData.client.workCurrency
                );

                if (hasMaxClickPrice(strategy) && constants) {
                    strategy.errors[
                        StrategyErrorType.maxClickPrice
                    ] = validateMaxClickPrice(strategy, constants, currency);
                }
                break;
            }

            case StrategyEditorActionType.CHANGE_ATTRIBUTION_MODEL: {
                const editor = draft.editors[action.editorId];
                const strategy =
                    editor && editor.strategies[editor.strategyType];
                if (!strategy) {
                    return;
                }

                strategy.attributionModel = action.attributionModel;

                break;
            }

            case StrategyEditorActionType.TOGGLE_OPTIMIZATION_PARAMETERS: {
                const editor = draft.editors[action.editorId];
                if (editor) {
                    editor.isOptimizationParamsOpen = !editor.isOptimizationParamsOpen;
                }
                break;
            }

            case StrategyEditorActionType.TOGGLE_DAILY_BUDGET_PARAMETERS: {
                const editor = draft.editors[action.editorId];
                if (editor) {
                    editor.isDailyBudgetParamsOpen = !editor.isDailyBudgetParamsOpen;
                }
                break;
            }

            case StrategyEditorActionType.TOGGLE_AUXILIARY_PARAMETERS: {
                const editor = draft.editors[action.editorId];
                if (editor) {
                    editor.isAuxiliaryParamsOpen = !editor.isAuxiliaryParamsOpen;
                }
                break;
            }

            case StrategyEditorActionType.APPLY_VALIDATION_RESULT: {
                const { validationResult, editorId } = action;
                const editor = draft.editors[editorId];

                const strategy = editor.strategies[editor.strategyType];
                if (!strategy) {
                    // такой ситуации быть не может, так как если editor
                    // проинициализирован - то он проинициализирован со стратегией
                    break;
                }
                strategy.errors = validationResult;

                break;
            }

            case StrategyEditorActionType.REVERT_EDITING: {
                const { editorId, strategy } = action;
                const editor = draft.editors[editorId];

                if (editor) {
                    editor.strategies = {
                        [strategy.type]: mapStrategyForEditor(strategy)
                    };
                    editor.strategyType = strategy.type;
                    editor.isOpen = false;
                }

                break;
            }

            default:
                return;
        }
    });
};
